import { useEffect, useCallback, useMemo } from 'react';
import { useThirtyThirtyQueue } from './useThirtyThirtyQueue';
import { useFocusTimer } from '../contexts/FocusTimerContext';
import { useHabitTemplates } from './useHabitTemplates';
import { supabase } from '../lib/supabase';
/**
 * useMaunaQueue Hook
 *
 * Integrates the 30/30 queue system with Mauna's FocusTimerContext.
 * Syncs active queue items with the currentStep and manages timer state
 * across both systems.
 */
export function useMaunaQueue() {
    // Get the base queue hook
    const queueHook = useThirtyThirtyQueue();
    // Get focus timer context
    const focusTimer = useFocusTimer();
    // Get habit templates hook
    const habitTemplates = useHabitTemplates();
    /**
     * Sync active queue item to FocusTimerContext when it changes
     * Handles all item types: steps, task_lists, lengths, breaths, etc.
     */
    useEffect(() => {
        if (!queueHook.activeItem) {
            // No active item - clear current step
            if (focusTimer.currentStep) {
                focusTimer.setCurrentStep(null);
            }
            return;
        }
        /**
         * For steps, fetch full data. For other types, use queue data directly.
         */
        const syncQueueItemData = async () => {
            try {
                // For steps, fetch detailed data with breaths (instance-aware)
                if (queueHook.activeItem.itemType === 'step') {
                    const { data: stepData, error } = await supabase
                        .from('steps')
                        .select(`
              id,
              label,
              duration,
              color,
              icon,
              mantra,
              starting_ritual,
              ending_ritual,
              audio_url,
              audio_type,
              instance_date,
              is_template,
              template_step_id,
              length_id,
              breaths (
                id,
                step_id,
                name,
                completed,
                is_running,
                start_time,
                end_time,
                paused_time,
                total_time_seconds,
                time_estimation_seconds,
                position,
                instance_date
              )
            `)
                        .eq('id', queueHook.activeItem.itemId)
                        .single();
                    if (error) {
                        console.error('Error fetching step data for queue sync:', error);
                        return;
                    }
                    if (stepData) {
                        // Convert step data to FocusTimer format (including instance metadata for habits)
                        const stepObj = stepData;
                        const step = {
                            id: stepObj.id,
                            user_id: stepObj.user_id || '',
                            task_list_id: stepObj.task_list_id || null,
                            label: stepObj.label,
                            description: stepObj.description,
                            duration: stepObj.duration,
                            completed: stepObj.completed || false,
                            color: stepObj.color || '#3B82F6',
                            icon: stepObj.icon,
                            breaths: stepObj.breaths || [],
                            starting_ritual: stepObj.starting_ritual,
                            ending_ritual: stepObj.ending_ritual,
                            mantra: stepObj.mantra,
                            // Habit instance tracking
                            instance_date: stepObj.instance_date,
                            is_template: stepObj.is_template,
                            template_step_id: stepObj.template_step_id,
                            length_id: stepObj.length_id
                        };
                        focusTimer.setCurrentStep(step);
                    }
                }
                else {
                    // For non-step types (task_list, length, breath, etc.), create a generic task object
                    // using the denormalized queue item data
                    const genericTask = {
                        id: queueHook.activeItem.itemId,
                        user_id: '',
                        task_list_id: null,
                        label: queueHook.activeItem.label,
                        description: `${queueHook.activeItem.itemType} • ${queueHook.activeItem.duration || 0} seconds`,
                        duration: queueHook.activeItem.duration || 0,
                        completed: false,
                        color: queueHook.activeItem.color || '#8B5CF6',
                        icon: queueHook.activeItem.icon || '📋',
                        breaths: [],
                        starting_ritual: undefined,
                        ending_ritual: undefined,
                        mantra: undefined
                    };
                    focusTimer.setCurrentStep(genericTask);
                }
            }
            catch (err) {
                console.error('Failed to sync queue item data:', err);
            }
        };
        syncQueueItemData();
    }, [queueHook.activeItem?.itemId, queueHook.activeItem?.itemType, queueHook.activeItem?.label, queueHook.activeItem?.duration, queueHook.activeItem?.color, queueHook.activeItem?.icon]);
    /**
     * Sync timer running state between queue and focus timer
     * When focus timer starts/pauses, update queue hook's timer
     */
    useEffect(() => {
        // If focus timer is running but queue timer isn't, start queue timer
        if (focusTimer.isRunning && !queueHook.isRunning && queueHook.activeItem) {
            queueHook.startTimer();
        }
        // If focus timer is not running but queue timer is, pause queue timer
        else if (!focusTimer.isRunning && queueHook.isRunning) {
            queueHook.pauseTimer();
        }
    }, [focusTimer.isRunning, queueHook.isRunning, queueHook.activeItem]);
    /**
     * When user switches steps via FocusTimer (setCurrentStep), update active queue item
     * This handles the case where user clicks on a step in the Mauna UI directly
     */
    useEffect(() => {
        // Check if the current step in focus timer matches the active queue item
        if (focusTimer.currentStep && queueHook.activeItem?.itemType === 'step') {
            // If they're different steps, we don't need to do anything here
            // The user has intentionally switched steps outside the queue
            // The queue will handle switching when user clicks on a queue item
        }
    }, [focusTimer.currentStep, queueHook.activeItem]);
    /**
     * Enhanced addToQueue that integrates with Mauna's UI
     * For habit_group types (recurring habits), automatically clone template steps/breaths
     */
    const addToQueueEnhanced = useCallback(async (itemType, itemId) => {
        try {
            // Check if this is a habit_group (length with type habit_group)
            if (itemType === 'length') {
                const { data: length, error: lengthError } = await supabase
                    .from('lengths')
                    .select('type')
                    .eq('id', itemId)
                    .single();
                if (lengthError)
                    throw lengthError;
                // If it's a recurring habit group with templates, clone them for today
                if (length?.type === 'habit_group') {
                    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
                    try {
                        console.log(`Cloning templates for habit group ${itemId} for date ${today}`);
                        const clonedSteps = await habitTemplates.cloneTemplateSteps(itemId, today);
                        console.log(`Successfully cloned ${clonedSteps.length} template steps for habit instance`);
                    }
                    catch (cloneErr) {
                        console.warn('Error cloning habit templates (continuing anyway):', cloneErr);
                        // Don't throw - user can still add the habit to queue without templates
                    }
                }
            }
            // Add to queue normally
            await queueHook.addToQueue(itemType, itemId);
            // Toast notification would go here if needed
        }
        catch (err) {
            console.error('Error adding to queue:', err);
            throw err;
        }
    }, [queueHook, habitTemplates]);
    /**
     * Enhanced switchActiveTask that handles step-specific logic
     */
    const switchActiveTaskEnhanced = useCallback(async (queueId) => {
        try {
            await queueHook.switchActiveTask(queueId);
            // Pause focus timer during switch
            focusTimer.pauseTimer();
            // Re-sync the step data
            const queueItem = queueHook.queue.find(item => item.queueId === queueId);
            if (queueItem?.itemType === 'step') {
                const { data: stepData } = await supabase
                    .from('steps')
                    .select(`
              id,
              label,
              duration,
              color,
              icon,
              mantra,
              starting_ritual,
              ending_ritual,
              audio_url,
              audio_type,
              instance_date,
              is_template,
              template_step_id,
              length_id,
              breaths (
                id,
                step_id,
                name,
                completed,
                is_running,
                start_time,
                end_time,
                paused_time,
                total_time_seconds,
                time_estimation_seconds,
                position,
                instance_date
              )
            `)
                    .eq('id', queueItem.itemId)
                    .single();
                if (stepData) {
                    const stepObj = stepData;
                    const step = {
                        id: stepObj.id,
                        user_id: stepObj.user_id || '',
                        task_list_id: stepObj.task_list_id || null,
                        label: stepObj.label,
                        description: stepObj.description,
                        duration: stepObj.duration,
                        completed: stepObj.completed || false,
                        color: stepObj.color || '#3B82F6',
                        icon: stepObj.icon,
                        breaths: stepObj.breaths || [],
                        starting_ritual: stepObj.starting_ritual,
                        ending_ritual: stepObj.ending_ritual,
                        mantra: stepObj.mantra,
                        // Habit instance tracking
                        instance_date: stepObj.instance_date,
                        is_template: stepObj.is_template,
                        template_step_id: stepObj.template_step_id,
                        length_id: stepObj.length_id
                    };
                    focusTimer.setCurrentStep(step);
                }
            }
        }
        catch (err) {
            console.error('Error switching active task:', err);
            throw err;
        }
    }, [queueHook, focusTimer]);
    /**
     * Merge both hook returns
     * Return queue hook interface but with enhanced methods and integration
     */
    const memoizedReturn = useMemo(() => ({
        // Queue state
        queue: queueHook.queue,
        activeItem: queueHook.activeItem,
        completedItems: queueHook.completedItems,
        // Timer state
        isRunning: queueHook.isRunning,
        currentTime: queueHook.currentTime,
        // Queue operations (enhanced)
        addToQueue: addToQueueEnhanced,
        removeFromQueue: queueHook.removeFromQueue,
        reorderQueue: queueHook.reorderQueue,
        switchActiveTask: switchActiveTaskEnhanced,
        // Timer operations
        startTimer: queueHook.startTimer,
        pauseTimer: queueHook.pauseTimer,
        resetTimer: queueHook.resetTimer,
        // Completion & Syncing
        completeActiveTask: queueHook.completeActiveTask,
        syncQueueOrderToSource: queueHook.syncQueueOrderToSource,
        refreshQueue: queueHook.refreshQueue,
        // Loading/error
        isLoading: queueHook.isLoading,
        error: queueHook.error,
        // Mauna-specific integration info
        currentStep: focusTimer.currentStep,
        isFocusTimerRunning: focusTimer.isRunning
    }), [
        queueHook.queue,
        queueHook.activeItem,
        queueHook.completedItems,
        queueHook.isRunning,
        queueHook.currentTime,
        queueHook.removeFromQueue,
        queueHook.reorderQueue,
        queueHook.startTimer,
        queueHook.pauseTimer,
        queueHook.resetTimer,
        queueHook.completeActiveTask,
        queueHook.syncQueueOrderToSource,
        queueHook.refreshQueue,
        queueHook.isLoading,
        queueHook.error,
        addToQueueEnhanced,
        switchActiveTaskEnhanced,
        focusTimer.currentStep,
        focusTimer.isRunning
    ]);
    return memoizedReturn;
}
